import { Box } from "lucide-react";
import { memo, useState, useEffect, ReactNode } from "react";
import { Popover } from "@radix-ui/themes";

interface Props {
  name: string;
  onEdit: (name: string, quantity: number) => void;
  onDelete: () => void;
  stock?: number;
  readOnly?: boolean;
  feedbackButton?: ReactNode;
}

const partCardContent = (
  name: string,
  stock: number | undefined,
  feedbackButton?: ReactNode,
) => (
  <>
    <div className="part-card-content">
      <div className="part-icon-container">
        <Box className="part-icon" width={24} height={24} />
        <div className="part-icon-glow"></div>
      </div>
      <div className="part-info">
        <h3 className="part-name">{name}</h3>
        <div className="part-stock">
          {stock !== undefined ? `In stock: ${stock} units` : 'In stock: -- units'}
        </div>
      </div>
      <div className="part-actions">
        {feedbackButton ?? null}
      </div>
    </div>
    <div className="part-card-border"></div>
  </>
);

function Part({ name, onEdit, onDelete, stock, readOnly = false, feedbackButton }: Props) {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [editFields, setEditFields] = useState({ name, stock });

  useEffect(() => {
    setEditFields({ name, stock: stock ?? 1 });
  }, [name, stock]);

  const handleFieldChange = (field: string, value: string | number) => {
    setEditFields(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    onEdit(editFields.name, editFields.stock || 1);
    setPopoverOpen(false);
  };

  const card = (
    <div
      className={
        readOnly
          ? "part-card group relative cursor-default transition-all duration-300"
          : "part-card group relative cursor-pointer transition-all duration-300 hover:scale-[1.02] hover:shadow-lg"
      }
    >
      {partCardContent(name, stock, feedbackButton)}
    </div>
  );

  if (readOnly) {
    return card;
  }

  return (
    <Popover.Root open={popoverOpen} onOpenChange={setPopoverOpen}>
      <Popover.Trigger>{card}</Popover.Trigger>
      <Popover.Content width="320px">
        <div className="flex flex-col gap-2 bg-(--color-modal-bg) p-2 rounded-md opacity-100">
          <label className="text-xs">Name</label>
          <input className="input" value={editFields.name} onChange={e => handleFieldChange('name', e.target.value)} />
          <label className="text-xs">Stock (units)</label>
          <input className="input" type="number" value={editFields.stock ?? ''} onChange={e => handleFieldChange('stock', e.target.value ? Number(e.target.value) : 0)} />
          <div className="flex justify-end gap-2">
            <button className="btn btn-outline mt-2" onClick={() => {
              onDelete();
              setPopoverOpen(false);
            }}>Delete</button>
            <button className="btn btn-outline mt-2" onClick={() => {
              handleSave();
              setPopoverOpen(false);
            }}>Save</button>
          </div>
        </div>
      </Popover.Content>
    </Popover.Root>
  );
}

export default memo(Part);
